import { useState, useRef, useEffect } from 'react';
import QrScanner from 'qr-scanner';
import { apiFetch } from '../designer/config/api';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    printerIp: string;
    printerSerial?: string;
    printerModel?: string;
    isAdmin: boolean;
    onRegisterSuccess: (data: { model: string; serial: string; ip: string; distributorCode: string }) => void;
}

const ALLOWED_MODELS = ['MC110', 'MC210', 'MC310', 'XID8100', 'XID8300', 'XID8600', 'XL8300'];
const DISTRIBUTOR_CODE = 'IDBSMX';

// Simulación de base de datos de seriales válidos
const VALID_SERIALS = ['12345', '67890', 'ABCDE', 'MATICA001', '0003608'];

export default function PrinterRegistrationModal({
    isOpen,
    onClose,
    printerIp,
    printerSerial = '',
    printerModel = 'MC210',
    isAdmin,
    onRegisterSuccess
}: Props) {
    const [model, setModel] = useState(printerModel);
    const [serial, setSerial] = useState(printerSerial);
    const [distributorCode, setDistributorCode] = useState(DISTRIBUTOR_CODE);
    const [error, setError] = useState('');
    const [isScanning, setIsScanning] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);
    const qrScannerRef = useRef<QrScanner | null>(null);

    // Actualizar estados cuando cambien las props
    useEffect(() => {
        if (isOpen) {
            setModel(printerModel);
            setSerial(printerSerial);
            setDistributorCode(DISTRIBUTOR_CODE);
            setError('');
        }
    }, [isOpen, printerModel, printerSerial]);

    if (!isOpen) return null;

    const startQrScan = async () => {
        setIsScanning(true);
        setError('');

        try {
            if (videoRef.current) {
                qrScannerRef.current = new QrScanner(
                    videoRef.current,
                    (result: any) => {
                        // El QR puede contener datos concatenados, ej: "MC210|0003608|IDBSMX"
                        const data = result.data;
                        console.log('QR Data:', data);

                        // Intentar parsear si está en formato delimitado
                        if (data.includes('|')) {
                            const parts = data.split('|');
                            if (parts.length >= 3) {
                                setModel(parts[0].trim());
                                setSerial(parts[1].trim());
                                setDistributorCode(parts[2].trim());
                            } else if (parts.length === 2) {
                                setModel(parts[0].trim());
                                setSerial(parts[1].trim());
                            } else {
                                setSerial(parts[0].trim());
                            }
                        } else {
                            // Si no tiene delimitador, asumir que es solo el serial
                            setSerial(data.trim());
                        }

                        stopQrScan();
                    },
                    {
                        returnDetailedScanResult: true,
                        highlightScanRegion: true,
                        highlightCodeOutline: true,
                    }
                );

                await qrScannerRef.current.start();
            }
        } catch (err) {
            console.error('QR Scanner error:', err);
            setError('Failed to start camera. Please check permissions.');
            setIsScanning(false);
        }
    };

    const stopQrScan = () => {
        if (qrScannerRef.current) {
            qrScannerRef.current.stop();
            qrScannerRef.current.destroy();
            qrScannerRef.current = null;
        }
        setIsScanning(false);
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        // 1. Validar Código de Distribuidor
        if (distributorCode !== DISTRIBUTOR_CODE) {
            setError('Invalid Distributor Code. Please check your documentation.');
            return;
        }

        // 2. Validar Serial (Solo si no es Admin)
        if (!isAdmin) {
            if (!VALID_SERIALS.includes(serial)) {
                setError('Serial Number not authorized. Please contact support to add this device.');
                return;
            }
        }

        // 3. Obtener Account ID del localStorage
        const accountId = localStorage.getItem('accountId');
        if (!accountId) {
            setError('Account ID not found. Please log in again.');
            return;
        }

        // 4. Enviar registro al servidor
        try {
            const data = await apiFetch('/api/printer/register', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ip_address: printerIp,
                    model: model,
                    serial_number: serial,
                    firmware: '', // Se puede obtener del descubrimiento si está disponible
                    account_id: accountId,
                    type: 2, // 1 = USB, 2 = Network
                    IsRegistered: true // Explicitly register the printer
                }),
            });

            if (!data.Success) {
                setError(data.Message || 'Failed to register printer');
                return;
            }

            // Éxito
            alert(`Printer Registered Successfully!\n\nModel: ${model}\nSerial: ${serial}\nIP: ${printerIp}\n\nDevice is now linked to your account.`);

            // Llamar al callback para refrescar la lista
            onRegisterSuccess({ model, serial, ip: printerIp, distributorCode });

            onClose();
        } catch (err: any) {
            console.error('Registration error:', err);
            setError(err.message || 'Failed to connect to server. Please try again.');
        }
    };

    const handleClose = () => {
        stopQrScan();
        onClose();
    };

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <h3>Register Matica Device</h3>
                <p className="modal-subtitle">Target IP: {printerIp}</p>

                {isScanning ? (
                    <div className="qr-scanner-container">
                        <video ref={videoRef} className="qr-video"></video>
                        <p className="qr-instruction">Point camera at QR code on printer</p>
                        <button type="button" onClick={stopQrScan} className="btn-secondary">
                            Cancel Scan
                        </button>
                    </div>
                ) : (
                    <form onSubmit={handleRegister}>
                        <div className="form-group">
                            <label>Distributor Code (Required)</label>
                            <input
                                value={distributorCode}
                                onChange={(e) => setDistributorCode(e.target.value)}
                                placeholder="Enter Distributor Code"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Printer Model</label>
                            <select value={model} onChange={(e) => setModel(e.target.value)} className="form-select">
                                {ALLOWED_MODELS.map(m => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Serial Number</label>
                            <div className="input-with-button">
                                <input
                                    value={serial}
                                    onChange={(e) => setSerial(e.target.value)}
                                    placeholder="Enter Device Serial Number"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={startQrScan}
                                    className="btn-scan-qr"
                                    title="Scan QR Code"
                                >
                                    📷 Scan QR
                                </button>
                            </div>
                            {isAdmin && <small className="admin-hint">🛡️ Super Admin Mode: Serial validation bypassed</small>}
                        </div>

                        {error && <div className="error-message">{error}</div>}

                        <div className="modal-actions">
                            <button type="button" onClick={handleClose} className="btn-secondary">Cancel</button>
                            <button type="submit" className="btn-primary">Register Device</button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
